import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { CatalogApi } from '@backstage/catalog-client';
import {
  ErrorApi,
  errorApiRef,
  toastApiRef,
  ToastApi,
  useApi,
} from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Account,
  AccountEntity,
  AccountUserRole,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  ProvidersApi,
  providersApiRef,
} from '@internal/backstage-plugin-catalog-devops-react';
import { ActionTypes } from './types';
import { AccountApi, accountApiRef } from '../../api';

interface AccountsContextProviderProps {
  children: ReactNode;
}

type LoadCompletedAccountsAction = {
  type: ActionTypes.LOAD_COMPLETED;
  payload: AccountEntity[];
};

type DeleteAccountAction = {
  type: ActionTypes.DELETE;
};

type DeleteCompletedAccountAction = {
  type: ActionTypes.DELETE_COMPLETED;
};

type CreateCompletedAccountAction = {
  type: ActionTypes.CREATE_COMPLETED;
};

type EditCompletedAccountAction = {
  type: ActionTypes.EDIT_COMPLETED;
};

type CreateAccountAction = {
  type: ActionTypes.CREATE;
  payload: Account;
};

type EditAccountAction = {
  type: ActionTypes.EDIT;
  payload: Account;
};

type SelectAccountAction = {
  type: ActionTypes.SELECT;
  payload: AccountEntity;
};

type UnselectAccountAction = {
  type: ActionTypes.UNSELECT;
};

type AccountsAction =
  | LoadCompletedAccountsAction
  | DeleteAccountAction
  | CreateAccountAction
  | EditAccountAction
  | SelectAccountAction
  | UnselectAccountAction
  | DeleteCompletedAccountAction
  | CreateCompletedAccountAction
  | EditCompletedAccountAction;

type AccountsState = {
  accounts: AccountEntity[];
  selectedAccount: AccountEntity | undefined;
  status: string;
  newAccount: Account | undefined;
};

type AccountsContextType = {
  accounts: AccountEntity[];
  selectedAccount: AccountEntity | undefined;
  isLoading: boolean;
  deleteAccount(): void;
  createAccount(account: Account): void;
  editAccount(account: Account): void;
  selectAccount(account: AccountEntity): void;
  unselectAccount(): void;
};

const initialAccountsState = {
  accounts: [],
  selectedAccount: undefined,
  status: 'initial_load',
  newAccount: undefined,
};

export const AccountsContext = createContext<AccountsContextType | null>(null);

export function AccountsContextProvider(
  props: Readonly<AccountsContextProviderProps>,
) {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const accountApi = useApi<AccountApi>(accountApiRef);
  const providersApi = useApi<ProvidersApi>(providersApiRef);
  const toastApi = useApi<ToastApi>(toastApiRef);
  const errorApi = useApi<ErrorApi>(errorApiRef);

  const accountsReducer = (
    state: AccountsState,
    action: AccountsAction,
  ): AccountsState => {
    switch (action.type) {
      case ActionTypes.LOAD_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.accounts = action.payload;
        stateCopy.status = 'wait';
        return stateCopy;
      }
      case ActionTypes.DELETE: {
        const stateCopy = { ...state };
        stateCopy.status = 'delete';
        return stateCopy;
      }
      case ActionTypes.DELETE_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.selectedAccount = undefined;
        stateCopy.status = 'load';
        return stateCopy;
      }
      case ActionTypes.CREATE: {
        const stateCopy = { ...state };
        stateCopy.status = 'create';
        stateCopy.newAccount = action.payload;
        return stateCopy;
      }
      case ActionTypes.CREATE_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.status = 'load';
        stateCopy.newAccount = undefined;
        return stateCopy;
      }
      case ActionTypes.EDIT: {
        const stateCopy = { ...state };
        stateCopy.newAccount = action.payload;
        stateCopy.status = 'edit';
        return stateCopy;
      }
      case ActionTypes.EDIT_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.newAccount = undefined;
        stateCopy.selectedAccount = undefined;
        stateCopy.status = 'load';
        return stateCopy;
      }
      case ActionTypes.SELECT: {
        const stateCopy = { ...state };
        stateCopy.selectedAccount = action.payload;
        return stateCopy;
      }
      case ActionTypes.UNSELECT: {
        const stateCopy = { ...state };
        stateCopy.selectedAccount = undefined;
        return stateCopy;
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(accountsReducer, initialAccountsState);

  useEffect(() => {
    const accountFilter = { kind: 'account' };
    if (state.status === 'initial_load') {
      catalogApi
        .getEntities({ filter: accountFilter })
        .then(value => {
          dispatch({
            type: ActionTypes.LOAD_COMPLETED,
            payload: value.items as AccountEntity[],
          });
        })
        .catch(error => {
          // A rejected catalog request must still leave the terminal state,
          // otherwise the account list spins forever with no error message.
          errorApi.post(error as Error);
          dispatch({ type: ActionTypes.LOAD_COMPLETED, payload: [] });
        });
    }

    if (state.status === 'load') {
      new Promise(resolve => setTimeout(resolve, 2000))
        .then(() =>
          catalogApi.getEntities({ filter: accountFilter }).then(value => {
            dispatch({
              type: ActionTypes.LOAD_COMPLETED,
              payload: value.items as AccountEntity[],
            });
          }),
        )
        .catch(error => {
          errorApi.post(error as Error);
          dispatch({ type: ActionTypes.LOAD_COMPLETED, payload: [] });
        });
    }
  }, [catalogApi, errorApi, state.status]);

  useEffect(() => {
    const createEntity = async () => {
      if (state.status !== 'create') return;
      if (state.newAccount === undefined) return;

      const response = await accountApi.createAccountEntity(state.newAccount);

      if (response.succeeded) {
        await providersApi.triggerProvider('Account');

        if (
          state.newAccount.Users?.some(
            user => user.Role === AccountUserRole.ADMIN,
          )
        ) {
          await providersApi.triggerProvider('User');
        }

        toastApi.post({
          title: `Account "${state.newAccount.AccountId}" was created successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while creating account.`,
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.CREATE_COMPLETED });
    };

    createEntity();
  }, [accountApi, providersApi, toastApi, state.status, state.newAccount]);

  useEffect(() => {
    const editEntity = async () => {
      if (state.status !== 'edit') return;
      if (state.newAccount === undefined) return;
      if (state.selectedAccount === undefined) {
        dispatch({ type: ActionTypes.EDIT_COMPLETED });
        return;
      }

      const response = await accountApi.editAccountEntity(
        state.selectedAccount.spec.dynamic_uuid,
        state.newAccount,
      );

      if (response.succeeded) {
        await providersApi.triggerProvider('Account');
        toastApi.post({
          title: `Account "${state.newAccount.AccountId}" was updated successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while updating account.`,
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.EDIT_COMPLETED });
    };

    editEntity();
  }, [
    accountApi,
    providersApi,
    toastApi,
    state.status,
    state.selectedAccount,
    state.newAccount,
  ]);

  useEffect(() => {
    const deleteEntity = async () => {
      if (state.status !== 'delete') return;

      // Leaving the reducer stuck in 'delete' would spin the list forever;
      // complete the action even when nothing is selected.
      if (state.selectedAccount === undefined) {
        dispatch({ type: ActionTypes.DELETE_COMPLETED });
        return;
      }

      const response = await accountApi.deleteAccountEntity(
        state.selectedAccount.spec.dynamic_uuid,
      );

      if (response.succeeded) {
        await providersApi.triggerProvider('Account');
        toastApi.post({
          title: `Account "${state.selectedAccount.metadata.name}" was deleted successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while deleting account.`,
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.DELETE_COMPLETED });
    };

    deleteEntity();
  }, [accountApi, providersApi, toastApi, state.status, state.selectedAccount]);

  const deleteAccount = () => dispatch({ type: ActionTypes.DELETE });
  const createAccount = (account: Account) =>
    dispatch({ type: ActionTypes.CREATE, payload: account });
  const editAccount = (account: Account) =>
    dispatch({ type: ActionTypes.EDIT, payload: account });
  const selectAccount = (accountEntity: AccountEntity) =>
    dispatch({ type: ActionTypes.SELECT, payload: accountEntity });
  const unselectAccount = () => dispatch({ type: ActionTypes.UNSELECT });

  const context = useMemo(
    () => ({
      accounts: state.accounts,
      selectedAccount: state.selectedAccount,
      isLoading: state.status !== 'wait',
      deleteAccount,
      createAccount,
      editAccount,
      selectAccount,
      unselectAccount,
    }),
    [state.accounts, state.selectedAccount, state.status],
  );

  return (
    <AccountsContext.Provider value={context}>
      {props.children}
    </AccountsContext.Provider>
  );
}
